<template>
  <div>
    <div class="flex items-center gap-2 mb-2">
      <input
        v-model.trim="search"
        type="text"
        placeholder="Filter by filename..."
        class="border border-gray-200 rounded px-2 py-1 text-xs w-56 focus:outline-none focus:border-gray-400"
        @keyup.enter="load(0)"
      />
      <button
        class="text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="load(0)"
      >
        Refresh
      </button>
      <span v-if="total !== null" class="ml-auto text-xs text-gray-400">
        {{ rangeLabel }}
      </span>
    </div>

    <div v-if="error" class="text-xs text-red-600 py-2">
      {{ error }}
      <button class="underline ml-1" @click="load(page * pageSize)">Retry</button>
    </div>

    <table v-else class="w-full text-xs">
      <thead>
        <tr class="text-left text-gray-500 border-b border-gray-100">
          <th class="py-1.5 pr-3 font-medium">File</th>
          <th v-if="!schemaId" class="py-1.5 pr-3 font-medium">Schema</th>
          <th class="py-1.5 pr-3 font-medium w-8"></th>
          <th class="py-1.5 pr-3 font-medium text-right w-20">Rows</th>
          <th class="py-1.5 font-medium w-36">When</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows" :key="row.id">
          <tr
            class="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
            @click="toggleExpand(row.id)"
          >
            <td class="py-1.5 pr-3">
              <span class="text-gray-800 break-all">{{ row.sheet_filename }}</span>
              <span v-if="row.sheet_name" class="block text-gray-400">{{ row.sheet_name }}</span>
            </td>
            <td v-if="!schemaId" class="py-1.5 pr-3 font-mono text-gray-600">{{ row.schema_name }}</td>
            <td class="py-1.5 pr-3">
              <span
                class="inline-block h-2 w-2 rounded-full"
                :class="statusDotClass(row.status)"
                :title="statusKey(row.status)"
              ></span>
            </td>
            <td class="py-1.5 pr-3 text-right font-mono text-gray-600">{{ formatCount(row.rows_loaded) }}</td>
            <td class="py-1.5 text-gray-500 whitespace-nowrap">{{ formatDateTime(row.created_at) }}</td>
          </tr>
          <tr v-if="expanded.has(row.id)" :key="'x' + row.id">
            <td :colspan="schemaId ? 4 : 5" class="px-3 py-2 bg-gray-50/70 border-b border-gray-100">
              <div class="flex items-start gap-6 text-xs">
                <div class="space-y-0.5 min-w-0">
                  <p class="text-gray-500">
                    Status <span class="text-gray-800">{{ statusKey(row.status) }}</span>
                    <template v-if="row.parse_duration_seconds != null">
                      · {{ row.parse_duration_seconds.toFixed(1) }}s
                    </template>
                    <template v-if="row.columns_loaded"> · {{ row.columns_loaded }} columns</template>
                  </p>
                  <p v-if="row.s3_key" class="text-gray-400 font-mono break-all">{{ row.s3_key }}</p>
                  <p v-if="row.error_message" class="text-red-600 break-all">{{ row.error_message }}</p>
                </div>
                <button
                  v-if="canManage"
                  class="ml-auto shrink-0 text-xs px-2 py-1 rounded border"
                  :class="row.reparse
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-400'"
                  @click.stop="toggleReparse(row)"
                >
                  {{ row.reparse ? 'Reparse queued — cancel' : 'Queue reparse' }}
                </button>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && !rows.length">
          <td :colspan="schemaId ? 4 : 5" class="py-6 text-center text-gray-400">
            No parse history{{ search ? ' matching your filter' : '' }}.
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="py-3 text-center text-xs text-gray-400">Loading…</div>

    <div v-if="total > pageSize" class="flex items-center justify-end gap-2 pt-2 text-xs">
      <button
        class="px-2 py-1 rounded border border-gray-200 text-gray-600 disabled:opacity-40"
        :disabled="page === 0 || loading"
        @click="load((page - 1) * pageSize)"
      >
        Prev
      </button>
      <button
        class="px-2 py-1 rounded border border-gray-200 text-gray-600 disabled:opacity-40"
        :disabled="(page + 1) * pageSize >= total || loading"
        @click="load((page + 1) * pageSize)"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  formatDateTime, formatCount, statusDotClass, statusKey,
} from '@/utils/etlFormat';

/**
 * The one parse-history table — used globally, scoped to a routine, or scoped
 * to a schema. Five calm columns; everything else (duration, error, S3 key,
 * reparse) lives in the click-to-expand row.
 */
export default {
  name: 'ParseHistoryTable',
  props: {
    routineId: { type: Number, default: null },
    schemaId: { type: Number, default: null },
    pageSize: { type: Number, default: 50 },
    canManage: { type: Boolean, default: false },
  },
  data() {
    return {
      rows: [],
      total: null,
      page: 0,
      loading: false,
      error: '',
      search: '',
      expanded: new Set(),
      requestSeq: 0,
    };
  },
  computed: {
    rangeLabel() {
      if (!this.total) return '0 records';
      const start = this.page * this.pageSize + 1;
      const end = Math.min((this.page + 1) * this.pageSize, this.total);
      return `${start}–${end} of ${formatCount(this.total)}`;
    },
  },
  mounted() {
    this.load(0);
  },
  methods: {
    formatDateTime,
    formatCount,
    statusDotClass,
    statusKey,
    refresh() {
      this.load(this.page * this.pageSize);
    },
    async load(startRow) {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = '';
      try {
        const body = {
          start_row: startRow,
          end_row: startRow + this.pageSize,
        };
        if (this.routineId) body.routine_id = this.routineId;
        if (this.schemaId) body.schema_id = this.schemaId;
        if (this.search) {
          body.filter_model = {
            sheet_filename: { filterType: 'text', filter: this.search },
          };
        }
        const { data } = await axios.post('/api/etl_module/sheet-parse-history/list/', body);
        if (seq !== this.requestSeq) return; // a newer request superseded this one
        this.rows = data.rowData;
        this.total = data.lastRow;
        this.page = Math.floor(startRow / this.pageSize);
        this.expanded = new Set();
      } catch (err) {
        if (seq !== this.requestSeq) return;
        this.error = err?.response?.data?.error || 'Failed to load parse history.';
      }
      if (seq === this.requestSeq) this.loading = false;
    },
    toggleExpand(id) {
      const next = new Set(this.expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expanded = next;
    },
    async toggleReparse(row) {
      const previous = row.reparse;
      row.reparse = !previous;
      try {
        await axios.patch(`/api/etl_module/sheet-parse-history/${row.id}/`, { reparse: row.reparse });
      } catch {
        row.reparse = previous;
        this.error = 'Failed to update reparse flag.';
      }
    },
  },
};
</script>
